import { Currency, FeedType, Language } from "@finbackoffice/enums";
import { IRuntimeConfig, BannerSizeEnum } from "@finbackoffice/fe-core";
import { BrowserStorageIdsEnum } from "@finbackoffice/site-core";
import { ICategoryFeedResponse } from "@finbackoffice/websocket-client";
import { DEFAULT_ROOT_LOCALE } from "./constants";

export const getCategoriesEventsBelongTo = (
    categories: ICategoryFeedResponse[],
    amount: number,
): number[] => {
    const result: number[] = [];

    let count = 0;
    for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        count += category.games_count;
        result.push(category.id);
        if (count >= amount) {
            break;
        }
    }
    return result;
};

export const getCurrentLocale = (
    runtimeConfig: IRuntimeConfig,
    currentLocale: string | undefined,
) => {
    currentLocale = currentLocale || runtimeConfig.COMMON_SITE_CONFIGS.defaultLocale;

    if (currentLocale === DEFAULT_ROOT_LOCALE) {
        return runtimeConfig.COMMON_SITE_CONFIGS.defaultLocale;
    }
    return currentLocale as Language;
};

export function generateUUID() {
    if (typeof window === "undefined") {
        // server
        return "";
    }

    // browser
    const localStorageValue = localStorage.getItem(BrowserStorageIdsEnum.DEVICE_ID);
    if (localStorageValue) {
        return localStorageValue;
    }
    // @ts-expect-error TS(2365): Operator '+' cannot be applied to types 'number[]'... Remove this comment to see the full error message
    const uuid = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: any) =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    );
    localStorage.setItem(BrowserStorageIdsEnum.DEVICE_ID, uuid);
    return uuid;
}

export const getBannerSize = (size: BannerSizeEnum) => {
    switch (size) {
        case BannerSizeEnum.Medium:
            return {
                width: 360,
                height: 220,
            };
        case BannerSizeEnum.Narrow:
            return {
                width: 414,
                height: 100,
            };
        default:
            return {
                width: 414,
                height: 450,
            };
    }
};

export function scrollWithOffset(
    element: HTMLElement,
    offset = 0,
    waitForLayout = false,
): Promise<void> {
    const performScroll = () => {
        let parent = element.parentElement;
        while (parent && parent !== document.body) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            const isScrollable = overflowY === "auto" || overflowY === "scroll";

            if (isScrollable && parent.scrollHeight > parent.clientHeight) {
                const elementRect = element.getBoundingClientRect();
                const parentRect = parent.getBoundingClientRect();
                const scrollTop = elementRect.top - parentRect.top + parent.scrollTop - offset;
                parent.scrollTo({ top: scrollTop, behavior: "smooth" });
                return;
            }
            parent = parent.parentElement;
        }

        const y = element.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
    };

    return new Promise((resolve) => {
        if (waitForLayout) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    performScroll();
                    resolve();
                });
            });
        } else {
            performScroll();
            resolve();
        }
    });
}

export function scrollToTop() {
    const scrollRestorationAuto = window.history.scrollRestoration === "auto";
    if (window.scrollY !== 0 || scrollRestorationAuto) {
        // Run after browser's scroll restoration completes
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
        });
    }
}

export function concatPathWithoutDuplicate(basePath: string, nextPath: string) {
    const baseParts = basePath.split("/").filter(Boolean);
    const nextParts = nextPath.split("/").filter(Boolean);

    const maxOverlap = Math.min(baseParts.length, nextParts.length);
    let overlap = 0;

    for (let i = maxOverlap; i > 0; i--) {
        const baseTail = baseParts.slice(-i).join("/");
        const nextHead = nextParts.slice(0, i).join("/");

        if (baseTail === nextHead) {
            overlap = i;
            break;
        }
    }

    return "/" + [...baseParts, ...nextParts.slice(overlap)].join("/");
}

export const getCurrencyTempId = (currency: Currency): number => {
    let hash = 0;
    for (let i = 0; i < currency.length; i++) {
        hash = (hash << 5) - hash + currency.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }
    return -Math.abs(hash); // Ensure negative
};

export const getSportClassName = (sportId: number, feedType?: FeedType): string => {
    const prefix = feedType === FeedType.BetConstruct ? "bc-" : "";
    return `sportId-${prefix}${sportId}`;
};
